/**
 * BookmarkBatchProvider — batches bookmark-status checks across many cards on
 * a single page (portal/list pages). Without this, each BookmarkButton on the
 * page would fire its own /api/bookmarks/check request, blowing through the
 * 60/min rate limit on pages with dozens of cards.
 *
 * Usage:
 *   <BookmarkBatchProvider items={allItemsOnPage}>
 *     ...cards rendering BookmarkButton...
 *   </BookmarkBatchProvider>
 *
 * Inside this provider, BookmarkButton reads status from the context instead
 * of fetching individually. Standalone BookmarkButton (no provider) keeps
 * its single-item check behavior.
 */
import { ReactElement, ReactNode, createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  bookmarksAPI,
  BookmarkItemType,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

export interface BookmarkBatchItem {
  itemType: BookmarkItemType;
  itemId: string;
}

interface BookmarkStatus {
  bookmarked: boolean;
  id: number | null;
}

interface BookmarkBatchContextValue {
  /** True if a batch query is active for this subtree. */
  inBatch: boolean;
  /** Returns the cached status for an item, or undefined if not yet loaded. */
  getStatus: (itemType: BookmarkItemType, itemId: string) => BookmarkStatus | undefined;
  /** Optimistically update the batch cache after a mutation. */
  setStatus: (itemType: BookmarkItemType, itemId: string, status: BookmarkStatus) => void;
}

const BookmarkBatchContext = createContext<BookmarkBatchContextValue | null>(null);

export function useBookmarkBatch(): BookmarkBatchContextValue | null {
  return useContext(BookmarkBatchContext);
}

interface BatchResponseData {
  results: Record<string, number | null>;
}

interface BookmarkBatchProviderProps {
  items: BookmarkBatchItem[];
  children: ReactNode;
}

export default function BookmarkBatchProvider({
  items,
  children,
}: BookmarkBatchProviderProps): ReactElement {
  const { isAuthenticated } = useAuth();

  // Dedupe items by (type:id) and cap at 100 (backend batch limit).
  const dedupedKeys = Array.from(
    new Set(items.map((i) => `${i.itemType}:${i.itemId}`))
  ).slice(0, 100);

  // Stable cache key from the sorted list of items.
  const itemsKey = [...dedupedKeys].sort().join(',');

  const { data, isLoading } = useQuery<BatchResponseData>({
    queryKey: ['bookmarks-batch', itemsKey],
    queryFn: async () => {
      if (dedupedKeys.length === 0) return { results: {} };
      const payload = dedupedKeys.map((k) => {
        const [item_type, ...rest] = k.split(':');
        return {
          item_type: item_type as BookmarkItemType,
          item_id: rest.join(':'),
        };
      });
      const res = await bookmarksAPI.checkBatch(payload);
      return res.data;
    },
    enabled: isAuthenticated && dedupedKeys.length > 0,
    staleTime: 60_000,
  });

  const getStatus = (itemType: BookmarkItemType, itemId: string): BookmarkStatus | undefined => {
    if (!data) return undefined;
    const key = `${itemType}:${itemId}`;
    if (!(key in data.results)) return undefined;
    const id = data.results[key];
    return { bookmarked: id !== null, id };
  };

  const setStatus = (itemType: BookmarkItemType, itemId: string, status: BookmarkStatus): void => {
    if (!data) return;
    // Mutate the cached batch data directly so all consumers re-render.
    const key = `${itemType}:${itemId}`;
    data.results[key] = status.id;
  };

  const value: BookmarkBatchContextValue = {
    inBatch: isAuthenticated && dedupedKeys.length > 0,
    getStatus,
    setStatus,
  };

  // Silence unused warning during loading state.
  void isLoading;

  return (
    <BookmarkBatchContext.Provider value={value}>
      {children}
    </BookmarkBatchContext.Provider>
  );
}
